import { ReactNode, useState } from 'react';

import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ShoppingCartOutlinedIcon from '@mui/icons-material/ShoppingCartOutlined';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { isNumber } from 'lodash-es';

import { useClientContext } from 'context/ClientContext';
import { usePedidoCompraContext } from 'context/PedidoCompraContext';
import {
    PedidoCompraAdicionarButton,
    PedidoCompraAmmount,
    PedidoCompraProdutoModal,
    PedidoCompraValue,
    PedidoDescontoChip,
    PedidoPrioritarioChip,
} from 'pages/compra/pedido/components';
import { ProdutoPedido } from 'pages/compra/pedido/types';
import { brlDecimal, camelCase } from 'util/format';

type InfoContainerProps = {
    value: ReactNode;
    subtitle: ReactNode;
    icon?: ReactNode;
    opacity?: number;
};

/**
 * Componente para exibir informações do produto.
 */
function InfoContainerList({ value, subtitle, icon, opacity = 1 }: InfoContainerProps) {
    return (
        <Box sx={{ width: 76, opacity, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                {icon}
                <Typography sx={{ fontSize: 14, lineHeight: 1 }}>{value}</Typography>
            </Box>

            <Typography variant="caption" sx={{ lineHeight: 1 }}>
                {subtitle}
            </Typography>
        </Box>
    );
}

export function PedidoCompraListProduct({ produtoPedido }: { produtoPedido: ProdutoPedido }) {
    const { modifyProdListQtd, getProdListQtd, isProdutoDisabled, getProdMinQtd, configPedidoValidateQtdMin, getProdCart } = usePedidoCompraContext();
    const { produto, qtMinimaCompra, peOferta, fgShowPeOferta, fgObrigatorio } = produtoPedido;
    const produtoDisabled = isProdutoDisabled(produtoPedido);
    const produtoCart = getProdCart(produto.idProduto);
    const produtoQtd = produtoCart ? produtoCart.qtProdutoList : 0;
    const prodMinQtd = getProdMinQtd(qtMinimaCompra);
    const [openProdutoModal, setOpenProdutoModal] = useState<boolean>(false);
    const { tipoPedido } = useClientContext();
    const isTipoPedidoPadrao = tipoPedido === 'padrao';
    const isTipoPedidoFuncionario = tipoPedido === 'funcionario';
    const quantityTooltip =
        produtoDisabled || !configPedidoValidateQtdMin ? '' : `Quantidade deve ser múltipla de ${brlDecimal(prodMinQtd || 0, 0, 3)}`;

    return (
        <>
            <PedidoCompraProdutoModal open={openProdutoModal} setOpen={setOpenProdutoModal} produto={produtoPedido} />

            <Tooltip followCursor={true} title={produtoDisabled ? 'Produto indisponível para compra' : ''}>
                <Paper
                    variant="elevation"
                    elevation={2}
                    sx={{
                        width: '100%',
                        height: '100%',
                        p: 1.5,
                        display: 'flex',
                        overflow: 'hidden',
                        opacity: produtoDisabled ? 0.6 : 1,
                        alignItems: 'center',
                        gap: 1.5,
                    }}
                >
                    <Box
                        onClick={() => (isTipoPedidoFuncionario ? null : setOpenProdutoModal(true))}
                        sx={{
                            'height': 68,
                            'width': 68,
                            'borderRadius': 1.5,
                            'border': '1px solid',
                            'borderColor': 'divider',
                            'backgroundColor': 'grey.50',
                            'backgroundPosition': 'center',
                            'backgroundRepeat': 'no-repeat',
                            'backgroundSize': 'contain',
                            'backgroundImage': `url("${produto.lkImagem}")`,
                            'cursor': 'pointer',
                            'display': 'flex',
                            'alignItems': 'center',
                            'justifyContent': 'center',
                            'overflow': 'hidden',
                            'transition': 'all 0.2s cubic-bezier(0.23, 1, 0.32, 1)',
                            '&:hover': {
                                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                borderColor: 'primary.main',
                            },
                            '&:active': {
                                transform: 'scale(0.97)',
                            },
                        }}
                    >
                        {!produto.lkImagem && <InsertPhotoOutlinedIcon sx={{ fontSize: 32, color: 'grey.300' }} />}
                    </Box>

                    <Box sx={{ mr: 'auto', gap: 0.5, display: 'flex', flexDirection: 'column' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography
                                sx={{
                                    'fontSize': '1.4rem',
                                    'lineHeight': 1.2,
                                    'overflow': 'hidden',
                                    'cursor': 'pointer',
                                    'whiteSpace': 'nowrap',
                                    'textOverflow': 'ellipsis',
                                    'maxWidth': 400,
                                    'transition': 'all 0.2s cubic-bezier(0.23, 1, 0.32, 1)',
                                    '&:hover': {
                                        color: 'primary.main',
                                    },
                                    '&:active': {
                                        transform: 'scale(0.985)',
                                        opacity: 0.8,
                                    },
                                }}
                                onClick={() => (isTipoPedidoFuncionario ? null : setOpenProdutoModal(true))}
                            >
                                {camelCase(produto.dsProduto, 2)}
                            </Typography>

                            {!!peOferta && !!fgShowPeOferta && <PedidoDescontoChip peOferta={peOferta} size="small" />}

                            {fgObrigatorio && <PedidoPrioritarioChip size="small" />}
                        </Box>

                        <Box sx={{ display: 'flex', gap: 1 }}>
                            {isTipoPedidoPadrao && (
                                <>
                                    <InfoContainerList
                                        opacity={isNumber(produtoPedido.estoqueLoja?.qtSaldoAtual) ? 1 : 0.5}
                                        subtitle="Estoque na Loja"
                                        icon={<Inventory2OutlinedIcon sx={{ fontSize: 14 }} />}
                                        value={
                                            <>
                                                {brlDecimal(produtoPedido.estoqueLoja?.qtSaldoAtual || 0, 0, 3)}{' '}
                                                {produto.unidadeMedida?.sgUnidadeMedida?.toLowerCase()}
                                            </>
                                        }
                                    />

                                    <InfoContainerList
                                        opacity={isNumber(produtoPedido.qtCoberturaEstoque) ? 1 : 0.5}
                                        subtitle="Cobertura de Estoque"
                                        icon={<DateRangeOutlinedIcon sx={{ fontSize: 14 }} />}
                                        value={`${produtoPedido.qtCoberturaEstoque || 0} dias`}
                                    />

                                    <InfoContainerList
                                        opacity={isNumber(produtoPedido.qtSugerida) ? 1 : 0.5}
                                        subtitle="Quantidade Sugerida"
                                        icon={<CodeOutlinedIcon sx={{ fontSize: 14 }} />}
                                        value={
                                            <>
                                                {brlDecimal(produtoPedido.qtSugerida || 0, 0, 3)}{' '}
                                                {produto.unidadeMedida?.sgUnidadeMedida?.toLowerCase()}
                                            </>
                                        }
                                    />
                                </>
                            )}

                            <InfoContainerList
                                opacity={isNumber(prodMinQtd) && configPedidoValidateQtdMin ? 1 : 0.5}
                                subtitle="Quantidade Mínima"
                                icon={<ShoppingCartOutlinedIcon sx={{ fontSize: 14 }} />}
                                value={
                                    <>
                                        {brlDecimal(prodMinQtd || 0, 0, 3)} {produto.unidadeMedida?.sgUnidadeMedida?.toLowerCase()}
                                    </>
                                }
                            />

                            {isTipoPedidoFuncionario && (
                                <InfoContainerList
                                    opacity={isNumber(produto.produtoEstoque?.qtEstoqueDisponivel) ? 1 : 0.5}
                                    subtitle="Quantidade Disponível"
                                    icon={<ShoppingCartOutlinedIcon sx={{ fontSize: 14 }} />}
                                    value={
                                        <>
                                            {brlDecimal(
                                                (produto.produtoEstoque?.qtEstoqueDisponivel > 0 && produto.produtoEstoque?.qtEstoqueDisponivel) || 0,
                                                0,
                                                3,
                                            )}{' '}
                                            {produto.unidadeMedida?.sgUnidadeMedida?.toLowerCase()}
                                        </>
                                    }
                                />
                            )}
                        </Box>
                    </Box>

                    <Box sx={{ width: 90, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', mr: 1 }}>
                        <Typography variant="body2">Valor Unitário</Typography>

                        <PedidoCompraValue value={produtoPedido.vlPrecoCompra} align="right" size="small" />
                    </Box>

                    <Box sx={{ width: 130, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <Tooltip title={quantityTooltip}>
                            <>
                                <Typography variant="body2">Quantidade</Typography>

                                <PedidoCompraAmmount getQtd={getProdListQtd} modifyQtd={modifyProdListQtd} produtoPedido={produtoPedido} />
                            </>
                        </Tooltip>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'flex-end', height: '100%', pb: 1 }}>
                        <PedidoCompraAdicionarButton
                            idProduto={produto.idProduto}
                            qtMinimaCompra={prodMinQtd}
                            produtoQtd={produtoQtd}
                            produtoCart={produtoCart}
                            produtoDisabled={produtoDisabled}
                        />
                    </Box>

                    <Box sx={{ width: 100, display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Typography variant="body2">Valor Total</Typography>

                        <PedidoCompraValue value={produtoQtd > 0 ? produtoQtd * produtoPedido.vlPrecoCompra : 0} align="right" size="small" />
                    </Box>
                </Paper>
            </Tooltip>
        </>
    );
}
